import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'cookies-js';
import { CookieConstant } from '../constants';
import EventHub from '../eventHub';
import type { ltCommon } from '@life-time-inc/enterprise-framework-core';
import { sessionService } from '@life-time-inc/enterprise-framework-core';

export default class ApiService implements ltCommon.IApiService {
	public loading = false;
	protected axiosInstance: AxiosInstance;
	private loadingCount = 0;
	private readonly source: string;
	constructor(source: string, axiosInstance: AxiosInstance) {
		this.source = source;
		this.axiosInstance = axiosInstance;
		this.axiosInstance.interceptors.request.use(
			(config: AxiosRequestConfig) => {
				const ltRequestConfig: ltCommon.IAxiosRequestConfig = config;
				this.updateLoading(true, ltRequestConfig.ignoreLoading);

				// If the ct (Customer Token) is passed in as a URL parameter do not
				// pass the SSO or Profile Token since it would be from the current user
				// and would be overriden/removed anyways in favor of the details in the ct.
				const ct = window.ef?.profileStore?.getInitialData()?.authToken;
				if (ct) {
					config.headers = $.extend({}, config.headers, { 'X-LTF-CT': ct });
					if (config.url === 'profile') {
						config.url = 'api';
					}
				} else {
					const sso = Cookies.get(CookieConstant.ssoId);
					const rememberMeToken = Cookies.get(CookieConstant.ltAuthorization);
					if (rememberMeToken) {
						config.headers = $.extend({}, config.headers, { 'X-REMEMBER': rememberMeToken });
						sessionService.invalidateProfileToken();
					}
					const profileToken = Cookies.get(CookieConstant.profileToken);
					if (profileToken) {
						config.headers = $.extend({}, config.headers, { 'X-LTF-PROFILE': profileToken });
					}
					let auth = Cookies.get(CookieConstant.ltAuthentication);

					if (ltRequestConfig.useJwt) {
						auth = sessionService.getCookie(CookieConstant.jwtToken);
					} else if (ltRequestConfig.useB2C) {
						const b2c = window.ef.b2cStore?.getInitialData();
						if (b2c?.account?.idToken) {
							auth = b2c.account.idToken;
						}
					}
					if (auth) {
						config.headers = $.extend({}, config.headers, { Authorization: auth });
					}
					if (sso && !auth && !rememberMeToken) {
						config.headers = $.extend({}, config.headers, { 'X-LTF-SSOID': sso });
						if (config.url === 'profile') {
							config.url = 'api';
						}
					}
				}

				return config;
			},
			(error: AxiosError) => {
				this.updateLoading(false, (error.config as ltCommon.IAxiosRequestConfig).ignoreLoading);

				return Promise.reject(error);
			}
		);

		this.axiosInstance.interceptors.response.use(
			(response: AxiosResponse) => {
				this.updateLoading(false, (response.config as ltCommon.IAxiosRequestConfig).ignoreLoading);

				return response;
			},
			(error: AxiosError) => {
				this.updateLoading(false, (error.config as ltCommon.IAxiosRequestConfig).ignoreLoading);

				return Promise.reject(error);
			}
		);
	}

	private updateLoading(increment: boolean, ignoreLoading?: boolean): void {
		if (!ignoreLoading) {
			this.loadingCount += increment ? 1 : -1;
			this.loading = this.loadingCount > 0;
		}

		EventHub.emit(this.source + '-loading', this.loading);
	}
}
